import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { object, bool, string } from 'prop-types';
import ReportList from './ReportList/ReportList';
import { Dropdown } from 'ps-design';
import ReportSearch from './ReportSearch/ReportSearch';
import ReportFilters from './ReportFilters/ReportFilters';
import { withRouter, Link } from 'react-router-dom';
import * as reportActionCreators from './ReportActions';
import FullPageSlideOut from '../../FullPageSlideOut/FullPageSlideOut';
import ReportTabs from './ReportTabs/ReportTabs';
import VisualizationSlideOut from './VisualizationSlideOut/VisualizationSlideOut';
import ReportBulkActions from './ReportBulkActions/ReportBulkActions';

import './ReportLibrary.scss';

class ReportLibraryContainer extends Component {
  static propTypes = {
    actions: object,
    reports: object,
    orgCode: string,
    viewType: string,
    reportWriterUrl: string,
    launchDarklyFeatureFlags: object,
    location: object,
    userId: string,
    userEmail: string,
    userSuperAdmin: bool,
    userSiteAdmin: bool,
    userGroupAdmin: bool,
    userReportViewer: bool,
    groupId: string,
    defaultGroupId: string,
    groupFunctionality: bool,
    visualizationFunctionality: bool,
    noReportsStatusImage: string,
    reportViewerRights: object
  };

  static defaultProps = {
    reportViewerRights: {}
  };

  constructor(props) {
    super(props);
    this.filterReports = this.filterReports.bind(this);
    this.reportBulkSelect = this.reportBulkSelect.bind(this);
    this.renderWithPermissions = this.renderWithPermissions.bind(this);
    this.renderWithoutPermissions = this.renderWithoutPermissions.bind(this);
    this.getFilteredPopularReports = this.getFilteredPopularReports.bind(this);
    this.state = {
      initialReportsLoaded: false
    };
  }

  filterReports(props = this.props) {
    let createdByFilter = props.reports.createdByFilter;
    // Don't limit to personal reports if user is searching or viewing favorites
    if (props.reports.textFilter || props.reports.favoriteFilter) {
      createdByFilter = null;
    }

    if (props.reports.popularFilter && !props.reports.collectionsLoading) {
      this.props.actions.getCollections({
        textFilter: props.reports.textFilter
      });
    } else if (!props.reports.popularFilter) {
      this.props.actions.getReportList({
        includeFilter: props.reports.includeFilter,
        scopeFilter: props.reports.scopeFilter,
        typeFilter: props.reports.typeFilter,
        textFilter: props.reports.textFilter,
        favoriteFilter: props.reports.favoriteFilter,
        createdByFilter: createdByFilter,
        groupIdFilter: props.reports.groupIdFilter,
        sortField: props.reports.sortField,
        sortDirection: props.reports.sortDirection
      });
    }
  }

  reportBulkSelect(report) {
    if (report.selectedForEdit) {
      this.props.actions.deselectReportBulkEdit(report);
    } else {
      this.props.actions.selectReportBulkEdit(report);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.reports.scopeFilter === 'group' &&
      nextProps.groupId &&
      nextProps.groupId === nextProps.defaultGroupId &&
      nextProps.reports.groupIdFilter
    ) {
      this.props.actions.setGroupIdFilter(null);
    } else if (
      nextProps.reports.scopeFilter === 'group' &&
      nextProps.groupId &&
      nextProps.groupId !== nextProps.defaultGroupId &&
      nextProps.groupId !== nextProps.reports.groupIdFilter
    ) {
      this.props.actions.setGroupIdFilter(nextProps.groupId);
    }

    if (
      nextProps.reports.favoriteFilter !== this.props.reports.favoriteFilter ||
      nextProps.reports.popularFilter !== this.props.reports.popularFilter ||
      nextProps.reports.scopeFilter !== this.props.reports.scopeFilter ||
      nextProps.reports.textFilter !== this.props.reports.textFilter ||
      nextProps.reports.createdByFilter !== this.props.reports.createdByFilter ||
      nextProps.reports.includeFilter !== this.props.reports.includeFilter ||
      nextProps.reports.groupIdFilter !== this.props.reports.groupIdFilter ||
      nextProps.reports.typeFilter !== this.props.reports.typeFilter ||
      (nextProps.reports.sortDirection !== this.props.reports.sortDirection &&
        nextProps.reports.sortField !== 'type') ||
      (nextProps.reports.sortField !== this.props.reports.sortField && nextProps.reports.sortField !== 'type')
    ) {
      this.filterReports(nextProps);
    }

    // if no favorites and we have report results, go back to full list
    // Only matters on initial load
    if (
      !this.state.initialReportsLoaded &&
      nextProps.reports.favoriteFilter &&
      nextProps.reports.reportsLoaded &&
      nextProps.reports.reports &&
      nextProps.reports.reports.edges?.length === 0
    ) {
      this.props.actions.setFavoriteFilter(false);
    }
    // Identify that reports have loaded initially
    if (!this.state.initialReportsLoaded && nextProps.reports.reportsLoaded && nextProps.reports.reports) {
      this.setState({
        initialReportsLoaded: true
      });
    }
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.reports !== this.props.reports;
  }

  componentDidMount() {
    //  Filter first on favorites, if no favorites, filter later on all
    //  MPE-3043: Report Viewer Needs to Default to a Scope they have permissions for
    const { userReportViewer, reportViewerRights, reports, actions, groupFunctionality } = this.props;

    let propsWillUpdate = false;
    if (userReportViewer && reports.scopeFilter === null) {
      if (reportViewerRights.allowViewStandard) {
        actions.setScopeFilter('global');
        propsWillUpdate = true;
      } else if (reportViewerRights.allowViewGroup && groupFunctionality) {
        actions.setScopeFilter('group');
        propsWillUpdate = true;
      } else if (reportViewerRights.allowViewSite) {
        actions.setScopeFilter('site');
        propsWillUpdate = true;
      }
    }

    //  If the user is a report viewer and the scope had to change, do not update now as this will trigger
    //  the default filter of 'favorites' when the component receives updated props.
    //  Other, the same query will trigger 2x
    if (!propsWillUpdate) this.filterReports(this.props);
    actions.getViewPreference();
    if (reports?.reportTypes?.length === 0) actions.getReportTypeList();
  }

  hasNoAvailableViews(props) {
    return (
      props.userReportViewer &&
      !props.reportViewerRights.allowViewGroup &&
      !props.reportViewerRights.allowViewStandard &&
      !props.reportViewerRights.allowViewSite
    );
  }

  getFilteredPopularReports() {
    const { collections, collectionsFilter, collectionTypeFilter } = this.props.reports;
    let filteredCollections = collections;
    if (collectionsFilter) {
      filteredCollections = filteredCollections.filter(collection => collectionsFilter.includes(collection.name));
    }

    if (collectionTypeFilter) {
      filteredCollections = filteredCollections.filter(collection => {
        if (collectionTypeFilter === 'Group') {
          return collection.org_group_id;
        } else if (collectionTypeFilter === 'Site') {
          return collection.organization_id && !collection.org_group_id;
        } else {
          return !collection.organization_id;
        }
      });
    }

    return filteredCollections;
  }

  renderWithPermissions() {
    const reportWriterUrl = this.props.reportWriterUrl ? this.props.reportWriterUrl : '/iframe/app/#reportWriter';
    const CREATE_NEW_URL = reportWriterUrl + '/create';
    const {
      actions,
      defaultGroupId,
      groupFunctionality,
      groupId,
      location,
      noReportsStatusImage,
      orgCode,
      reports,
      reportViewerRights,
      userEmail,
      userGroupAdmin,
      userId,
      userReportViewer,
      userSiteAdmin,
      userSuperAdmin,
      readOnlyUser,
      primaryRole,
      visualizationFunctionality,
      userRights,
      userGroupCount
    } = this.props;
    const {
      deleteReport,
      deselectReport,
      saveReportAs,
      setCreatedByFilter,
      setGroupIdFilter,
      setIncludeFilter,
      setScopeFilter,
      setSortField,
      setTextFilter,
      setTypeFilter,
      setViewPreference,
      sortReportsByType
    } = actions;
    const {
      collections,
      collectionsFilter,
      collectionsLoading,
      collectionTypeFilter,
      createdByFilter,
      favoriteFilter,
      hiddenCollections,
      includeFilter,
      initialReportsLoaded,
      reportCloneError,
      reportCloneId,
      reportDeleteError,
      reportsLoading,
      reportTypes,
      scopeFilter,
      selectedReport,
      sortDirection,
      sortField,
      textFilter,
      typeFilter,
      viewType
    } = reports;
    const createLink = (path, text) => (
      <Link
        className="pxl-dropdown__item"
        to={{
          pathname: `${CREATE_NEW_URL}/${path}`,
          state: { history: location.pathname }
        }}
      >
        {text}
      </Link>
    );
    const canPublishToSite =
      userSuperAdmin ||
      userSiteAdmin ||
      (userGroupAdmin && userRights.allow_group_admins_publish_to_site) ||
      (primaryRole === 'rw_user' && userRights.allow_regular_users_publish_to_site) ||
      (readOnlyUser && userRights.allow_restricted_users_publish_to_site);
    const canPublishToGroup =
      groupFunctionality &&
      userGroupCount > 0 &&
      (userSuperAdmin ||
        userSiteAdmin ||
        (userGroupAdmin && userRights.allow_group_admins_publish_to_group) ||
        (primaryRole === 'rw_user' && userRights.allow_regular_users_publish_to_group) ||
        (readOnlyUser && userRights.allow_restricted_users_publish_to_group));

    return (
      <section className="reports">
        <div className="reports__header">
          <h1 className="reports__title">Report Library</h1>
          {!userReportViewer && (
            <Dropdown
              btnClass="dropdown pxl-btn-primary"
              btnText="Create New"
              dropdownClass="reports__create-new"
              id="create-new-report"
            >
              {(userSuperAdmin || userSiteAdmin) && (
                <>
                  {createLink('site', 'Site')}
                  {canPublishToGroup && createLink('group', 'Group')}
                  {createLink('personal', 'Personal')}
                  {userSuperAdmin && createLink('internal', 'Internal')}
                </>
              )}
              {!userSuperAdmin && !userSiteAdmin && (
                <>
                  {userGroupAdmin && (
                    <>
                      {canPublishToSite && createLink('site', 'Site')}
                      {canPublishToGroup && createLink('group', 'Group')}
                      {createLink('personal', 'Personal')}
                    </>
                  )}

                  {primaryRole === 'rw_user' && (
                    <>
                      {canPublishToSite && createLink('site', 'Site')}
                      {canPublishToGroup && createLink('group', 'Group')}
                      {createLink('personal', 'Personal')}
                    </>
                  )}

                  {readOnlyUser && (
                    <>
                      {canPublishToSite && createLink('site', 'Site')}
                      {canPublishToGroup && createLink('group', 'Group')}
                      {createLink('personal', 'Personal')}
                    </>
                  )}
                </>
              )}
            </Dropdown>
          )}
          <ReportSearch
            initialReportsLoaded={initialReportsLoaded}
            setTextFilter={setTextFilter}
            textFilter={textFilter}
          />
          <ReportTabs
            favoriteFilter={this.props.reports.favoriteFilter}
            initialReportsLoaded={this.props.reports.initialReportsLoaded}
            popularFilter={this.props.reports.popularFilter}
            setFavoriteFilter={this.props.actions.setFavoriteFilter}
            setPopularFilter={this.props.actions.setPopularFilter}
          />
        </div>
        <ReportFilters
          collections={collections}
          collectionsFilter={collectionsFilter}
          collectionTypeFilter={collectionTypeFilter}
          createdByFilter={createdByFilter}
          defaultGroupId={defaultGroupId}
          favoriteFilter={favoriteFilter}
          groupFunctionality={groupFunctionality}
          groupId={groupId}
          hiddenCollections={hiddenCollections}
          includeFilter={includeFilter}
          initialReportsLoaded={initialReportsLoaded}
          popularFilter={this.props.reports.popularFilter}
          reports={reports.reports}
          reportTypes={reportTypes}
          reportViewerRights={reportViewerRights}
          scopeFilter={scopeFilter}
          setCollectionsFilter={actions.setCollectionsFilter}
          setCollectionTypeFilter={actions.setCollectionTypeFilter}
          setCreatedByFilter={setCreatedByFilter}
          setGroupIdFilter={setGroupIdFilter}
          setIncludeFilter={setIncludeFilter}
          setScopeFilter={setScopeFilter}
          setSortField={setSortField}
          setTypeFilter={setTypeFilter}
          setViewPreference={setViewPreference}
          sortDirection={sortDirection}
          sortField={sortField}
          textFilter={textFilter}
          toggleCollections={actions.toggleCollections}
          typeFilter={typeFilter}
          userGroupAdmin={userGroupAdmin}
          userId={userId}
          userReportViewer={userReportViewer}
          userSiteAdmin={userSiteAdmin}
          userSuperAdmin={userSuperAdmin}
          viewType={viewType}
          visualizationFunctionality={visualizationFunctionality}
        />
        <ReportBulkActions
          reports={this.props.reports.reports}
          {...this.props.actions}
          viewType={this.props.reports.viewType}
          popularFilter={this.props.reports.popularFilter}
          selectedPopularReports={this.props.reports.selectedPopularReports}
          refresh={this.filterReports}
          userSiteAdmin={userSiteAdmin}
          userGroupAdmin={userGroupAdmin}
        />
        {reports && (
          <ReportList
            collections={collectionsFilter || collectionTypeFilter ? this.getFilteredPopularReports() : collections}
            createdByFilter={createdByFilter}
            deleteCollection={actions.deleteCollection}
            groupId={groupId}
            hiddenCollections={hiddenCollections}
            launchDarklyFeatureFlags={this.props.launchDarklyFeatureFlags}
            noReportsStatusImage={noReportsStatusImage}
            onSelectPopular={actions.onSelectPopular}
            orgCode={orgCode}
            popularFilter={this.props.reports.popularFilter}
            refresh={this.filterReports}
            reportBulkSelect={this.reportBulkSelect}
            reports={reports.reports}
            reportsLoading={reportsLoading || collectionsLoading}
            reportUrl={reportWriterUrl}
            scopeFilter={scopeFilter}
            selectedPopularReports={reports.selectedPopularReports}
            setSortField={setSortField}
            sortDirection={sortDirection}
            sortField={sortField}
            sortReportsByType={sortReportsByType}
            textFilter={textFilter}
            toggleCollections={actions.toggleCollections}
            userEmail={userEmail}
            userGroupAdmin={userGroupAdmin}
            userId={userId}
            userReportViewer={userReportViewer}
            userSiteAdmin={userSiteAdmin}
            viewType={viewType}
          />
        )}
        <FullPageSlideOut
          fixedPosition={true}
          from="right"
          handleCloseSlideOut={deselectReport}
          height="calc(100vh)"
          isOpen={selectedReport ? true : false}
          maxWidth="900px"
          minWidth="700px"
          paddingTop="45px"
          width="70%"
        >
          <VisualizationSlideOut
            deleteReport={deleteReport}
            getReportEditable={this.props.actions.getReportEditable}
            hideSlideout={deselectReport}
            newReportId={reportCloneId}
            refresh={this.filterReports}
            report={selectedReport}
            reportCloneLoading={this.props.reports.reportCloneLoading}
            reportDeleteError={this.props.reports.reportDeleteError}
            reportDeleteLoading={this.props.reports.reportDeleteLoading}
            reportSaveError={this.props.reports.reportCloneError}
            reportWriterUrl={reportWriterUrl}
            saveReportAs={saveReportAs}
          />
        </FullPageSlideOut>
      </section>
    );
  }

  renderWithoutPermissions() {
    return (
      <section className="reports">
        <div className="reports__header">
          <h1 className="reports__title">Report Library</h1>
          <h3 className="reports__no-available">No reports available</h3>
        </div>
      </section>
    );
  }

  render() {
    if (this.hasNoAvailableViews(this.props)) {
      return this.renderWithoutPermissions();
    } else {
      return this.renderWithPermissions();
    }
  }
}

const mapStateToProps = ({ reports }) => ({ reports });

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(reportActionCreators, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ReportLibraryContainer));
